import type { CSSProperties, ReactElement } from 'react'
import { clamp } from 'lodash-es'
import { useRef, useState } from 'react'
import color from 'color'

import useEventHandler from '../../utils/hooks/useEventHandler'

function isValidColor(value: string) {
  try {
    // `color` throws an error when the value is not a valid color
    color(value)
    return true
  } catch {
    return false
  }
}
const gradient = (direction: string, color: string) => `linear-gradient(${direction}, transparent 0%, ${color} 100%)`
const hueGradient =
  'linear-gradient(to bottom, #ff0000 0%, #ffff00 20%, #00ff00 35%, #00ffff 50%, #0000ff 65%, #ff00ff 80%, #ff0000 100%)'
const defaultDimensions = { width: 300, height: 300, hueWidth: 20, spacing: 5 }

type CoordinatorProps = {
  coords: number[]
  width: number
  height: number
  onChange: ([x, y]: [number, number], boolean) => void
  children: ({ x, y, rx, ry, width, height }: Record<string, number>) => ReactElement
  className?: string
  style?: CSSProperties
}

function Coordinator({
  coords,
  children,
  width,
  height,
  style,
  className,
  onChange,
}: Readonly<CoordinatorProps>): ReactElement {
  const isPressed = useRef(false)
  const ref = useRef<HTMLDivElement>(null)

  useEventHandler('mousemove', (e: MouseEvent) => {
    if (isPressed.current) handleCoordChange(e)
  })

  useEventHandler('mouseup', (e: MouseEvent) => {
    if (isPressed.current) {
      isPressed.current = false
      handleCoordChange(e, true)
    }
  })

  const handleCoordChange = ({ clientX, clientY }: MouseEvent, complete?: boolean) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { top, left } = ref.current!.getBoundingClientRect()

    // get positions relative to node's `width` and `height`
    // clamping between 0 and 0.99 (using `1` would lead to an immediate jump to `0`)
    const rx = clamp((clientX - left) / width, 0, 0.99)
    const ry = clamp((clientY - top) / height, 0, 0.99)

    onChange([rx, ry], complete)
  }

  const [rx, ry] = coords

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...style, width, height }}
      onTouchMove={(e) => handleCoordChange(e as any)}
      onMouseDown={(event) => {
        event.preventDefault()
        isPressed.current = true
      }}
    >
      {children({ x: rx * width, y: ry * height, rx, ry, width, height })}
    </div>
  )
}

export type ColorpickerFieldProps = {
  value: string
  onChange: (value: string) => void
  onChangeComplete?: (value: string) => void
  className?: string
  dimensions?: {
    width: number
    height: number
    hueWidth: number
    spacing: number
  }
  presetColors?: {
    value: string
    name: string
  }[]
}

function ColorpickerField({
  value,
  onChange,
  onChangeComplete,
  className,
  dimensions: { width, height, hueWidth, spacing } = defaultDimensions,
  presetColors = [],
}: Readonly<ColorpickerFieldProps>): ReactElement {
  const [intermediateHexInput, setIntermediateHexInput] = useState<string | null>(null)

  const colorHsv = color(value).hsv()

  const { h, s, v } = colorHsv.object()

  const changeColor = (color: color, complete = false) => {
    if (isValidColor(color.string())) {
      requestAnimationFrame(() => {
        const preciseColor = color.hue(Math.round(color.hue())).hsl().alpha(1).string()

        onChange(preciseColor)
        if (complete && onChangeComplete) onChangeComplete(preciseColor)
      })
    }
  }

  const handleSvChange = ([x, y]: [number, number], complete: boolean) => {
    setIntermediateHexInput(null)
    changeColor(colorHsv.saturationv(x * 100).value(100 - y * 100), complete)
  }

  const handleHueChange = ([, y]: [number, number], complete: boolean) => {
    setIntermediateHexInput(null)
    changeColor(colorHsv.hue(y * 360), complete)
  }

  const styles = {
    base: className,
    baseSaturationValue: `${className}-saturation-value-canvas`,
    baseHue: `${className}-hue`,
    baseHexInput: `${className}-hex-input`,
    saturation: `${className}-saturation`,
    value: `${className}-value`,
    marker: 'marker',
    presets: `${className}-color-presets`,
  }

  const hueColor = colorHsv.saturationv(100).value(100).hex()

  const hexColorString = intermediateHexInput ?? colorHsv.hex()

  return (
    <div className={styles.base} data-testid="colorpicker-field">
      <Coordinator
        onChange={handleSvChange}
        className={styles.baseSaturationValue}
        width={width - (hueWidth + spacing)}
        height={height}
        coords={[s / 100, v / 100]}
      >
        {({ x, y, height }) => (
          <>
            <div className={styles.saturation} style={{ background: gradient('to right', hueColor) }} />
            <div className={styles.value} style={{ background: gradient('to bottom', '#000') }} />
            <div className={styles.marker} style={{ left: Math.round(x), top: Math.round(height - y) }} />
          </>
        )}
      </Coordinator>
      <Coordinator
        onChange={handleHueChange}
        className={styles.baseHue}
        coords={[0, h / 360]}
        width={hueWidth}
        height={height}
        style={{ background: hueGradient }}
      >
        {({ y }) => <div className={styles.marker} style={{ top: Math.round(y) }} />}
      </Coordinator>
      {presetColors?.length > 0 && (
        <div className={styles.presets}>
          {presetColors.map((newColor) => (
            <button
              type="button"
              key={newColor.name}
              title={newColor.name}
              style={{ backgroundColor: newColor.value }}
              onClick={() => changeColor(color(newColor.value), true)}
            />
          ))}
        </div>
      )}
      <input
        data-testid="colorpicker-hex-input"
        className={styles.baseHexInput}
        type="text"
        value={hexColorString}
        onBlur={() => {
          setIntermediateHexInput(null)
          if (onChangeComplete) onChangeComplete(value)
        }}
        onChange={(e) => {
          setIntermediateHexInput(e.target.value)

          if (isValidColor(e.target.value)) {
            onChange(color(e.target.value).hsl().round().string())
          }
        }}
      />
    </div>
  )
}

export default ColorpickerField
